/**
 * Semantic display ordering for billing tier axis values (billing checklist
 * gap #3). The backend emits `tiers.axes[].values` in DICTIONARY order —
 * `['1080p', '480p', '4k', '720p']` — because the price list, not a curated
 * table, is the display source. Rendering that verbatim puts 4k before 720p.
 *
 * The rule, exactly as agreed with the backend checklist:
 *
 *   - an axis whose value keys all LEAD WITH A DIGIT is numeric-feeling
 *     (resolutions, sizes) and gets a number-aware natural sort:
 *     480p → 720p → 1080p → 4k, 1024x1024 → 1024x1536 → 1536x1024;
 *   - any other axis (high/low/medium, novideoinput/includesvideoinput)
 *     keeps the backend order untouched — the frontend owns no vocabulary
 *     for those words and an alphabetical "improvement" would shuffle
 *     low/medium/high into nonsense.
 *
 * `4k` is the one place plain segment-wise natural sort is not enough:
 * numerically 4 < 480, which would rank 4k as the SMALLEST resolution. A
 * number whose alphabetic tail is exactly `k` reads as a ×1000 magnitude
 * (the video-resolution shorthand), so 4k compares as 4000 and lands after
 * 1080p. The multiplier applies only to a bare `k` tail — `480p` keeps its
 * `p` as an ordinary text segment.
 */

/** One `number-or-text` segment of a natural-sort key. */
type NaturalSegment = { readonly num: number } | { readonly text: string };

const SEGMENT_RE = /(\d+)|(\D+)/g;

/**
 * Split a value key into numeric and non-numeric runs, folding the
 * `<digits>k` shorthand into `<digits>×1000` (see module docs).
 */
function naturalSegments(key: string): readonly NaturalSegment[] {
  const segments: NaturalSegment[] = [];
  for (const match of key.toLowerCase().matchAll(SEGMENT_RE)) {
    const digits = match[1];
    if (digits !== undefined) {
      segments.push({ num: Number(digits) });
    } else {
      segments.push({ text: match[2] ?? '' });
    }
  }
  // Fold `4k` → 4000: a numeric segment whose following text segment is
  // exactly `k` (end of key) is a magnitude, not a suffix word.
  const last = segments[segments.length - 1];
  const beforeLast = segments[segments.length - 2];
  if (
    last !== undefined &&
    beforeLast !== undefined &&
    'text' in last &&
    last.text === 'k' &&
    'num' in beforeLast
  ) {
    segments.splice(segments.length - 2, 2, { num: beforeLast.num * 1000 });
  }
  return segments;
}

/**
 * Number-aware comparison of two axis value keys. Numeric runs compare as
 * numbers, text runs as plain strings, numbers sort before text at the
 * same position, and a shared prefix ranks the shorter key first. Equal
 * keys compare 0, so a stable sort keeps the backend's relative order.
 */
export function compareAxisValueKeys(a: string, b: string): number {
  const left = naturalSegments(a);
  const right = naturalSegments(b);
  const length = Math.min(left.length, right.length);
  for (let i = 0; i < length; i += 1) {
    const l = left[i];
    const r = right[i];
    if (l === undefined || r === undefined) {
      break;
    }
    if ('num' in l && 'num' in r) {
      if (l.num !== r.num) {
        return l.num - r.num;
      }
    } else if ('text' in l && 'text' in r) {
      if (l.text !== r.text) {
        return l.text < r.text ? -1 : 1;
      }
    } else {
      return 'num' in l ? -1 : 1;
    }
  }
  return left.length - right.length;
}

/**
 * Whether every value key on the axis leads with a digit — the gate for
 * semantic sorting. A single non-numeric key (or an empty axis) keeps the
 * whole axis in backend order: sorting HALF an axis semantically would
 * interleave two orderings nobody chose.
 */
function isNumericFeelingAxis(keys: readonly string[]): boolean {
  return keys.length > 0 && keys.every((key) => /^\d/.test(key));
}

/**
 * The display order of one axis's values: semantically sorted when the
 * axis is numeric-feeling, otherwise the backend order verbatim. Pure and
 * non-mutating — always returns a new array.
 */
export function orderAxisValues<T extends { readonly key: string }>(
  values: readonly T[],
): readonly T[] {
  if (!isNumericFeelingAxis(values.map((value) => value.key))) {
    return [...values];
  }
  return [...values].sort((a, b) => compareAxisValueKeys(a.key, b.key));
}
